import React, { useCallback } from "react"

import IconButton from "@mui/material/IconButton"
import Paper from "@mui/material/Paper"
import { useTheme } from "@mui/material/styles"
import { ChevronDown } from "lucide-react"

import { useToggle } from "@hooks/useToggle"

import { IconWrapper } from "@components/IconWrapper"

import ls from "@services/localStorage"

import { outer } from "@constants/shadows"
import { defaultTransition } from "@constants/transitions"

export interface MinimizableCardProps {
  children: React.ReactNode
  localStorageKey: string
  defaultMinimized?: boolean
  sx?: any // eslint-disable-line @typescript-eslint/no-explicit-any
}

export const MinimizableCard: React.FC<MinimizableCardProps> = ({
  children,
  localStorageKey,
  defaultMinimized = false,
  sx,
}) => {
  const theme = useTheme()

  const onToggleCallback = useCallback(
    (isMinimized: boolean) => {
      ls.setItem(localStorageKey, isMinimized)
    },
    [localStorageKey],
  )

  const [isMinimized, toggleMinimized] = useToggle({
    initialValue:
      ls.getItem(localStorageKey) === null
        ? defaultMinimized
        : Boolean(ls.getItem(localStorageKey)),
    onToggle: onToggleCallback,
  })

  return (
    <Paper
      sx={{
        alignItems: "center",
        borderRadius: `${theme.shape.borderRadius}px`,
        boxShadow: outer.sm,
        display: "flex",
        flexDirection: "row",
        gap: theme.spacing(1),
        justifyContent: "flex-end",
        p: theme.spacing(1),
        position: "relative",
        transition: defaultTransition(theme),

        "& .minimize-trigger": {
          opacity: 0,
        },

        "&:hover": {
          ".minimize-trigger": {
            opacity: 1,
            zIndex: 1,
          },
          "&:after": {
            content: '""',
            position: "absolute",
            top: 0,
            right: 0,
            zIndex: 0,
            background: theme.palette.background.paper,
            borderRadius: `${theme.shape.borderRadius}px`,
            boxShadow: outer.sm,
            width: theme.spacing(4.5),
            height: theme.spacing(4.5),
          },
        },

        ...(isMinimized && {
          height: theme.spacing(5),
          overflow: "hidden",

          ".minimize-trigger": {
            transform: "rotate(-180deg)",
          },

          ".minimize-hidden": {
            display: "none",
          },
        }),
        ...sx,
      }}
    >
      <IconButton
        className="minimize-trigger"
        onClick={toggleMinimized}
        size="small"
        sx={{
          position: "absolute",
          right: theme.spacing(0.75),
          top: theme.spacing(0.75),
        }}
      >
        <IconWrapper size={16}>
          <ChevronDown />
        </IconWrapper>
      </IconButton>
      {children}
    </Paper>
  )
}
